import React, { useState, useEffect } from 'react';
import { useAuth } from '../Contexts/AuthContext';
import '../Assets/Stylesheets/Pages/MarketingGenius/ProjectsSidebar.scss';
import API_BASE_URL from '../config/api';

const ProjectsSidebar = ({ 
  currentProjectId, 
  onSelectProject, 
  onNewProject, 
  isOpen, 
  onToggle 
}) => {
  const { getAccessToken } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');

  useEffect(() => {
    loadProjects();
    loadTemplates();
  }, []);

  const loadProjects = async () => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/marketing-genius/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleNewProject = async (template) => {
    setShowNewProjectModal(false);
    
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: template.name,
          template_type: template.id,
          description: template.description
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.project) {
          // Refresh projects list
          loadProjects();
          // Select the new project
          if (onSelectProject) {
            onSelectProject(data.project);
          }
        } else {
          alert('Failed to create project. Please try again.');
        }
      } else {
        alert('Failed to create project. Please try again.');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      alert('Error creating project. Please try again.');
    }
  };

  const handleEditProject = (project, e) => {
    e.stopPropagation(); // Prevent triggering select
    setEditingProjectId(project.id);
    setEditingTitle(project.title);
  };

  const handleSaveTitle = async (projectId, e) => {
    e.stopPropagation();
    
    if (!editingTitle.trim()) {
      alert('Project title cannot be empty');
      return;
    }
    
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editingTitle
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.project) {
          // Update project in list
          setProjects(projects.map(p => 
            p.id === projectId ? { ...p, title: data.project.title } : p
          ));
          setEditingProjectId(null);
          setEditingTitle('');
        }
      }
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Error updating project title');
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingProjectId(null);
    setEditingTitle('');
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation(); // Prevent triggering select
    
    if (!window.confirm('Archive this project?')) return;
    
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/marketing-genius/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        // Remove from list
        setProjects(projects.filter(p => p.id !== projectId));
        
        // If deleted project was selected, clear selection
        if (currentProjectId === projectId && onSelectProject) {
          onSelectProject(null);
        }
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getTemplateIcon = (templateType) => {
    const template = templates.find(t => t.id === templateType);
    return template ? template.icon : '💡';
  };

  return (
    <>
      {/* Sidebar */}
      <div className={`projects-sidebar ${isOpen ? 'open' : 'closed'}`}>
        {/* Header */}
        <div className="sidebar-header">
          <h3>Projects</h3>
          <button 
            className="new-project-btn" 
            onClick={() => setShowNewProjectModal(true)}
            title="New Project"
          >
            <span>+</span>
          </button>
        </div>

        {/* Projects List */}
        <div className="projects-list">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading projects...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="empty-state">
              <p>No projects yet</p>
              <button onClick={() => setShowNewProjectModal(true)}>
                Create your first project
              </button>
            </div>
          ) : (
            projects.map(project => (
              <div
                key={project.id}
                className={`project-item ${currentProjectId === project.id ? 'active' : ''}`}
                onClick={() => onSelectProject && onSelectProject(project)}
              >
                <div className="project-icon">
                  {getTemplateIcon(project.template_type)}
                </div>
                <div className="project-info">
                  {editingProjectId === project.id ? (
                    <div className="project-edit" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleSaveTitle(project.id, e);
                          if (e.key === 'Escape') handleCancelEdit(e);
                        }}
                        autoFocus
                        className="edit-title-input"
                      />
                      <div className="edit-actions">
                        <button 
                          className="save-btn"
                          onClick={(e) => handleSaveTitle(project.id, e)}
                          title="Save"
                        >
                          ✓
                        </button>
                        <button 
                          className="cancel-btn"
                          onClick={(e) => handleCancelEdit(e)}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="project-title">{project.title}</div>
                      <div className="project-meta">
                        <span className="message-count">{project.message_count || 0} messages</span>
                        <span className="separator">•</span>
                        <span className="last-active">{formatDate(project.last_message_at || project.created_at)}</span>
                      </div>
                    </>
                  )}
                </div>
                {editingProjectId !== project.id && (
                  <div className="project-actions">
                    <button
                      className="edit-project-btn"
                      onClick={(e) => handleEditProject(project, e)}
                      title="Rename project"
                    >
                      ⚙️
                    </button>
                    <button
                      className="delete-project-btn"
                      onClick={(e) => handleDeleteProject(project.id, e)}
                      title="Archive project"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Toggle Button (for mobile) */}
        <button className="sidebar-toggle" onClick={onToggle}>
          {isOpen ? '◀' : '▶'}
        </button>
      </div>

      {/* New Project Modal */}
      {showNewProjectModal && (
        <div className="modal-overlay" onClick={() => setShowNewProjectModal(false)}>
          <div className="new-project-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Start a New Project</h2>
              <button 
                className="close-modal-btn" 
                onClick={() => setShowNewProjectModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <p className="modal-description">
                Choose a template to get started, or create a custom project
              </p>
              
              <div className="templates-grid">
                {templates.map(template => (
                  <div
                    key={template.id}
                    className="template-card"
                    onClick={() => handleNewProject(template)}
                  >
                    <div className="template-icon">{template.icon}</div>
                    <div className="template-name">{template.name}</div>
                    <div className="template-description">{template.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectsSidebar;

